import { useState, useEffect, useRef } from "react";
import { Upload, Loader2, CheckCircle, AlertCircle, X, FileText, Eye, Download, Trash2, Building2, Calendar, Edit2 } from "lucide-react";
import { adminService, Client, Unidade, TipoDocumento } from "../../../services/adminService";
import { documentService, Document } from "../../../services/documentService";

export function DocumentUpload() {
  const [clients, setClients] = useState<Client[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    clientId: "", unidadeId: "", tipoDocumentoId: "", dataReferencia: "", nome: ""
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Estados para listagem de documentos
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClientForList, setSelectedClientForList] = useState("");
  
  // Estados para filtros da listagem
  const [filterTipoId, setFilterTipoId] = useState("");
  const [filterUnidadeId, setFilterUnidadeId] = useState("");
  const [filterDataInicio, setFilterDataInicio] = useState("");
  const [filterDataFim, setFilterDataFim] = useState("");
  const [listUnidades, setListUnidades] = useState<Unidade[]>([]);
  
  // Estados para modal de edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [editForm, setEditForm] = useState({ nome: "", tipoDocumentoId: "", unidadeId: "", dataReferencia: "" });
  const [editUnidades, setEditUnidades] = useState<Unidade[]>([]);

  useEffect(() => {
    loadClients();
    loadTiposDocumento();
  }, []);

  useEffect(() => {
    if (formData.clientId) {
      loadUnidades(formData.clientId);
    } else {
      setUnidades([]);
    }
  }, [formData.clientId]);

  // Carregar unidades quando cliente da listagem mudar
  useEffect(() => {
    if (selectedClientForList) {
      loadListUnidades(selectedClientForList);
      loadDocuments();
    } else {
      setDocuments([]);
      setListUnidades([]);
    }
  }, [selectedClientForList]);

  // Carregar documentos quando filtros mudarem
  useEffect(() => {
    if (selectedClientForList) {
      loadDocuments();
    }
  }, [currentPage, filterTipoId, filterUnidadeId, filterDataInicio, filterDataFim]);

  const loadListUnidades = async (clientId: string) => {
    try {
      const result = await adminService.listUnidades(clientId);
      setListUnidades(result);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadDocuments = async () => {
    if (!selectedClientForList) return;
    setIsLoadingDocs(true);
    try {
      const result = await adminService.listDocumentsByClient(selectedClientForList, { 
        page: currentPage, 
        limit: 10,
        tipoDocumentoId: filterTipoId ? parseInt(filterTipoId) : undefined,
        unidadeId: filterUnidadeId ? parseInt(filterUnidadeId) : undefined,
        dataInicio: filterDataInicio || undefined,
        dataFim: filterDataFim || undefined,
      });
      setDocuments(result.data);
      setTotalPages(result.pagination.totalPages);
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const clearFilters = () => {
    setFilterTipoId("");
    setFilterUnidadeId("");
    setFilterDataInicio("");
    setFilterDataFim("");
    setCurrentPage(1);
  };

  const handleDeleteDocument = async (id: string) => {
    if (!confirm("Deseja excluir este documento?")) return;
    try {
      await adminService.deleteDocument(id);
      setMessage({ type: "success", text: "Documento excluído!" });
      loadDocuments();
    } catch {
      setMessage({ type: "error", text: "Erro ao excluir documento" });
    }
  };

  const handleEditDocument = async (doc: Document) => {
    setEditingDoc(doc);
    // Carregar unidades do cliente do documento
    try {
      const unidadesResult = await adminService.listUnidades(selectedClientForList);
      setEditUnidades(unidadesResult);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
    setEditForm({
      nome: doc.nome,
      tipoDocumentoId: doc.tipoDocumento.id.toString(),
      unidadeId: doc.unidade?.id?.toString() || "",
      dataReferencia: doc.dataReferencia.split("T")[0],
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDoc) return;
    try {
      await adminService.updateDocument(editingDoc.id, {
        nome: editForm.nome,
        tipoDocumentoId: parseInt(editForm.tipoDocumentoId),
        unidadeId: editForm.unidadeId ? parseInt(editForm.unidadeId) : null,
        dataReferencia: editForm.dataReferencia,
      });
      setMessage({ type: "success", text: "Documento atualizado!" });
      setShowEditModal(false);
      setEditingDoc(null);
      loadDocuments();
    } catch {
      setMessage({ type: "error", text: "Erro ao atualizar documento" });
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingDoc(null);
  };

  const loadClients = async () => {
    try {
      const result = await adminService.listClients();
      setClients(result.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  };

  const loadUnidades = async (clientId: string) => {
    try {
      const result = await adminService.listUnidades(clientId);
      setUnidades(result);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
    }
  };

  const loadTiposDocumento = async () => {
    try {
      const result = await adminService.listTiposDocumento();
      setTiposDocumento(result.filter(t => t.active));
    } catch (error) {
      console.error("Erro ao carregar tipos:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setMessage({ type: "error", text: "Apenas arquivos PDF são permitidos" });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: "error", text: "Arquivo deve ter no máximo 10MB" });
        return;
      }
      setSelectedFile(file);
      if (!formData.nome) {
        setFormData((prev) => ({ ...prev, nome: file.name.replace(".pdf", "") }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !formData.clientId || !formData.tipoDocumentoId || !formData.dataReferencia) {
      setMessage({ type: "error", text: "Preencha todos os campos obrigatórios" });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const data = new FormData();
      data.append("file", selectedFile);
      data.append("clientId", formData.clientId);
      data.append("tipoDocumentoId", formData.tipoDocumentoId);
      data.append("dataReferencia", formData.dataReferencia);
      if (formData.unidadeId) data.append("unidadeId", formData.unidadeId);
      if (formData.nome) data.append("nome", formData.nome);

      await adminService.uploadDocument(data);
      setMessage({ type: "success", text: "Documento enviado com sucesso!" });
      setSelectedFile(null);
      setFormData({ clientId: "", unidadeId: "", tipoDocumentoId: "", dataReferencia: "", nome: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao enviar documento" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload de Documento</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
            <select value={formData.clientId} onChange={(e) => setFormData((prev) => ({ ...prev, clientId: e.target.value, unidadeId: "" }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" required>
              <option value="">Selecione um cliente</option>
              {clients.map((client) => (<option key={client.id} value={client.id}>{client.razaoSocial}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
            <select value={formData.unidadeId} onChange={(e) => setFormData((prev) => ({ ...prev, unidadeId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 disabled:bg-gray-100" disabled={!formData.clientId}>
              <option value="">Matriz</option>
              {unidades.map((unidade) => (<option key={unidade.id} value={unidade.id}>{unidade.nome}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento *</label>
            <select value={formData.tipoDocumentoId} onChange={(e) => setFormData((prev) => ({ ...prev, tipoDocumentoId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" required>
              <option value="">Selecione um tipo</option>
              {tiposDocumento.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nome}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data de Referência *</label>
            <input type="date" value={formData.dataReferencia} onChange={(e) => setFormData((prev) => ({ ...prev, dataReferencia: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" required />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Documento</label>
            <input type="text" value={formData.nome} onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))}
              placeholder="Ex: Laudo PMOC Janeiro 2024" className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400" />
          </div>
        </div>

        <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${selectedFile ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}>
          {selectedFile ? (
            <div className="flex items-center justify-center gap-3">
              <Upload className="w-8 h-8 text-green-500" />
              <p className="text-green-700 font-medium">{selectedFile.name}</p>
              <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="p-1 text-red-500 hover:bg-red-100 rounded-full" title="Remover arquivo">
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="font-medium text-gray-900">Clique para selecionar o arquivo</p>
              <p className="text-sm text-gray-500">Apenas PDF, máximo 10MB</p>
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />

        <button type="submit" disabled={isLoading || !selectedFile}
          className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</> : <><Upload className="w-5 h-5" /> Enviar Documento</>}
        </button>
      </form>

      {/* Listagem de Documentos */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Documentos Publicados</h3>
        
        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
            <select 
              value={selectedClientForList} 
              onChange={(e) => { setSelectedClientForList(e.target.value); setCurrentPage(1); setFilterUnidadeId(""); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
            >
              <option value="">Selecione um cliente</option>
              {clients.map((client) => (<option key={client.id} value={client.id}>{client.razaoSocial}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select 
              value={filterTipoId} 
              onChange={(e) => { setFilterTipoId(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              disabled={!selectedClientForList}
            >
              <option value="">Todos</option>
              {tiposDocumento.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nome}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
            <select 
              value={filterUnidadeId} 
              onChange={(e) => { setFilterUnidadeId(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              disabled={!selectedClientForList}
            >
              <option value="">Todas</option>
              <option value="0">Matriz</option>
              {listUnidades.map((unidade) => (<option key={unidade.id} value={unidade.id}>{unidade.nome}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
            <input 
              type="date" 
              value={filterDataInicio} 
              onChange={(e) => { setFilterDataInicio(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              disabled={!selectedClientForList}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
            <input 
              type="date" 
              value={filterDataFim} 
              onChange={(e) => { setFilterDataFim(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
              disabled={!selectedClientForList}
            />
          </div>
        </div>
        {(filterTipoId || filterUnidadeId || filterDataInicio || filterDataFim) && (
          <div className="mb-4">
            <button onClick={clearFilters} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              <X className="w-4 h-4" /> Limpar filtros
            </button>
          </div>
        )}

        {!selectedClientForList ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Selecione um cliente para visualizar os documentos</p>
          </div>
        ) : isLoadingDocs ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Nenhum documento encontrado para este cliente</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referência</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publicado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-gray-900">{doc.nome}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">{doc.tipoDocumento.nome}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-gray-600 text-sm">
                          <Building2 className="w-3 h-3" />
                          {doc.unidade?.nome || "Matriz"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-gray-600 text-sm">
                          <Calendar className="w-3 h-3" />
                          {new Date(doc.dataReferencia).toLocaleDateString("pt-BR")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {new Date(doc.createdAt).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => documentService.viewFile(doc.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Visualizar">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => documentService.downloadFile(doc.id, doc.arquivo)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Download">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEditDocument(doc)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg" title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteDocument(doc.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
                  Anterior
                </button>
                <span className="text-sm text-gray-600">Página {currentPage} de {totalPages}</span>
                <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50">
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Edição */}
      {showEditModal && editingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar Documento</h3>
              <button onClick={closeEditModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Documento *</label>
                <input type="text" value={editForm.nome} onChange={(e) => setEditForm(p => ({ ...p, nome: e.target.value }))} required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Documento *</label>
                <select value={editForm.tipoDocumentoId} onChange={(e) => setEditForm(p => ({ ...p, tipoDocumentoId: e.target.value }))} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900">
                  <option value="">Selecione um tipo</option>
                  {tiposDocumento.map((tipo) => (<option key={tipo.id} value={tipo.id}>{tipo.nome}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                <select value={editForm.unidadeId} onChange={(e) => setEditForm(p => ({ ...p, unidadeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900">
                  <option value="">Matriz</option>
                  {editUnidades.map((unidade) => (<option key={unidade.id} value={unidade.id}>{unidade.nome}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Referência *</label>
                <input type="date" value={editForm.dataReferencia} onChange={(e) => setEditForm(p => ({ ...p, dataReferencia: e.target.value }))} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeEditModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
